import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { type ChecklistTemplate, checklistsApi } from '@/api/checklists.api';
import { formatApiMessage } from '@/api/client';
import { type TemplateView, templatesApi } from '@/api/templates.api';
import { ChecklistBuilder } from '@/components/checklist/ChecklistBuilder';
import { ChecklistReview } from '@/components/checklist/ChecklistReview';
import { AppShell } from '@/components/layout/AppShell';
import { formatDateTime } from '@/lib/format';

export function TemplatesListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [active, setActive] = useState<TemplateView | null>(null);
  const [tab, setTab] = useState<'envelopes' | 'checklists'>('envelopes');

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates', 'list'],
    queryFn: () => templatesApi.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => templatesApi.delete(uuid),
    onSuccess: () => {
      toast.success(t('templates_list.template_deleted'));
      qc.invalidateQueries({ queryKey: ['templates', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="page-title">{t('templates_list.title')}</h1>
          <p className="page-subtitle">{t('templates_list.subtitle')}</p>
        </div>
        <button onClick={() => navigate('/envelopes')} className="btn btn-secondary">
          {t('envelope_detail.back_to_list')}
        </button>
      </div>

      <div className="tabs">
        <button
          className={`tab ${tab === 'envelopes' ? 'tab-active' : ''}`}
          onClick={() => setTab('envelopes')}
        >
          {t('checklist.tab_envelopes')}
        </button>
        <button
          className={`tab ${tab === 'checklists' ? 'tab-active' : ''}`}
          onClick={() => setTab('checklists')}
        >
          ☑️ {t('checklist.tab_title')}
        </button>
      </div>

      {tab === 'checklists' ? (
        <ChecklistTemplatesPanel />
      ) : isLoading ? (
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      ) : templates.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-3xl mb-2">📋</div>
          <h3 className="text-base font-semibold text-gray-900">{t('templates_list.no_templates')}</h3>
          <p className="text-sm text-muted mt-1 max-w-md mx-auto">
            {t('templates_list.no_templates_text')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              className="card flex flex-col gap-2 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-base font-semibold text-gray-900 truncate">{tpl.title}</h3>
                <span className="badge badge-muted text-[10px]">{t(`envelope.workflow_${tpl.workflowType}`)}</span>
              </div>
              {tpl.message && (
                <p className="text-[12px] text-muted line-clamp-2">{tpl.message}</p>
              )}
              <div className="flex items-center gap-3 text-[11px] text-muted">
                <span>{tpl.documents.length} {t('dashboard.docs_short')}</span>
                <span>·</span>
                <span>{tpl.recipients.length} {t('dashboard.roles_short')}</span>
              </div>
              <TriggerCodesEditor template={tpl} />
              <div className="text-[11px] text-muted">{t('common.updated')} {formatDateTime(tpl.updatedAt)}</div>
              <div className="flex gap-2 mt-2 pt-2 border-t border-border">
                <button
                  className="btn btn-primary btn-sm flex-1"
                  onClick={() => setActive(tpl)}
                >
                  {t('templates_list.instantiate')}
                </button>
                <button
                  className="btn btn-ghost btn-sm text-danger"
                  onClick={() => {
                    if (confirm(t('templates_list.delete_confirm', { name: tpl.title }))) {
                      deleteMutation.mutate(tpl.id);
                    }
                  }}
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {active && <InstantiateModal template={active} onClose={() => setActive(null)} />}
    </AppShell>
  );
}

/**
 * Checklist templates authored in the wizard. A template only becomes usable
 * once the envelope carrying its blank PDF completes its signing chain, so
 * pending ones are listed here too — greyed out — to show where they are.
 */
function ChecklistTemplatesPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reviewing, setReviewing] = useState<ChecklistTemplate | null>(null);
  const [editing, setEditing] = useState<ChecklistTemplate | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['checklists', 'templates', 'all'],
    // status=all so the author can watch a pending template make its way
    // through approval instead of wondering where it went.
    queryFn: () => checklistsApi.listTemplates({ status: 'all' }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['checklists', 'templates'] });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'archived' }) =>
      checklistsApi.setTemplateStatus(id, status),
    onSuccess: () => {
      toast.success(t('checklist.status_toast'));
      invalidate();
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  // Re-author an existing questionnaire. An approved (active) template is
  // updated in place for future fills — the signed PDF stays as the approval
  // record and already-filled responses keep their own schema snapshot.
  const editMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { title: string; description?: string; schema: ChecklistTemplate['schema'] };
    }) => checklistsApi.updateTemplate(id, payload),
    onSuccess: () => {
      toast.success(t('checklist.updated_toast'));
      setEditing(null);
      invalidate();
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => checklistsApi.deleteTemplate(id),
    onSuccess: () => {
      toast.success(t('checklist.deleted_toast'));
      invalidate();
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  if (isLoading) {
    return <div className="card text-center py-12 text-muted">{t('common.loading')}</div>;
  }

  if (items.length === 0) {
    return (
      <div className="card text-center py-12">
        <div className="text-3xl mb-2">☑️</div>
        <h3 className="text-base font-semibold text-gray-900">{t('checklist.empty')}</h3>
        <p className="text-sm text-muted mt-1 max-w-md mx-auto">{t('checklist.empty_hint')}</p>
      </div>
    );
  }

  const badgeClass = (status: ChecklistTemplate['status']) =>
    status === 'active'
      ? 'badge badge-success'
      : status === 'pending_approval'
        ? 'badge badge-warning'
        : 'badge badge-muted';

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((tpl) => (
          <div key={tpl.id} className="card flex flex-col gap-2 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-base font-semibold text-gray-900 truncate">{tpl.title}</h3>
              <span className={`${badgeClass(tpl.status)} text-[10px] shrink-0`}>
                {t(`checklist.status_${tpl.status}`)}
              </span>
            </div>
            {tpl.description && <p className="text-[12px] text-muted line-clamp-2">{tpl.description}</p>}
            <div className="flex items-center gap-3 text-[11px] text-muted">
              <span>{t('checklist.questions_n', { n: tpl.questionCount })}</span>
              {tpl.responseCount !== undefined && tpl.responseCount > 0 && (
                <>
                  <span>·</span>
                  <span>{t('checklist.used_n', { n: tpl.responseCount })}</span>
                </>
              )}
            </div>
            {tpl.status === 'pending_approval' && (
              <p className="text-[11px] text-warning">{t('checklist.pending_hint')}</p>
            )}
            {tpl.editedAfterApproval && (
              <p className="text-[11px] text-muted">✎ {t('checklist.edited_after_approval')}</p>
            )}
            <div className="text-[11px] text-muted">
              {t('common.updated')} {formatDateTime(tpl.updatedAt)}
            </div>
            <div className="flex gap-2 mt-2 pt-2 border-t border-border">
              <button className="btn btn-primary btn-sm flex-1" onClick={() => setReviewing(tpl)}>
                {t('checklist.review')}
              </button>
              {tpl.status !== 'archived' && (
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(tpl)}>
                  {t('common.edit')}
                </button>
              )}
              {tpl.status === 'active' && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => statusMutation.mutate({ id: tpl.id, status: 'archived' })}
                >
                  {t('checklist.archive')}
                </button>
              )}
              {tpl.status === 'archived' && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => statusMutation.mutate({ id: tpl.id, status: 'active' })}
                >
                  {t('checklist.activate')}
                </button>
              )}
              <button
                className="btn btn-ghost btn-sm text-danger"
                onClick={() => {
                  if (confirm(t('checklist.delete_confirm', { title: tpl.title }))) {
                    deleteMutation.mutate(tpl.id);
                  }
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">{t('checklist.edit_title')}</h2>
              <button
                onClick={() => setEditing(null)}
                className="text-muted hover:text-gray-900 text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-5">
              {editing.status === 'active' && (
                <div className="mb-3 rounded-md border border-amber-200 bg-warning-bg px-3 py-2 text-[12px] text-gray-700">
                  {t('checklist.edit_approved_hint')}
                </div>
              )}
              <ChecklistBuilder
                key={editing.id}
                initialTitle={editing.title}
                initialDescription={editing.description ?? ''}
                initialSchema={editing.schema}
                saving={editMutation.isPending}
                saveLabel={t('checklist.save_changes') as string}
                onSave={(payload) => editMutation.mutate({ id: editing.id, payload })}
                onCancel={() => setEditing(null)}
              />
            </div>
          </div>
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">{t('checklist.review')}</h2>
              <button
                onClick={() => setReviewing(null)}
                className="text-muted hover:text-gray-900 text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-5">
              <ChecklistReview
                title={reviewing.title}
                subtitle={reviewing.description ?? undefined}
                schema={reviewing.schema}
                blank
                download={{ kind: 'template', id: reviewing.id }}
              />
            </div>
            <div className="p-5 border-t border-border flex justify-end">
              <button onClick={() => setReviewing(null)} className="btn btn-secondary">
                {t('checklist.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TriggerCodesEditor({ template }: { template: TemplateView }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(template.triggerType ?? '');
  const [dept, setDept] = useState(template.triggerDept ?? '');
  const [form, setForm] = useState(template.triggerForm ?? '');

  const save = useMutation({
    mutationFn: () =>
      templatesApi.update(template.id, {
        triggerType: type || null,
        triggerDept: dept || null,
        triggerForm: form || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates', 'list'] });
      setOpen(false);
    },
  });

  const summary = [template.triggerType, template.triggerDept, template.triggerForm]
    .filter(Boolean)
    .join(' / ');

  return (
    <div className="text-[11px] border-t border-border pt-2">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="text-muted hover:text-primary text-left w-full"
          title={t('templates_list.trigger_hint')}
        >
          {summary ? `🎯 ${summary}` : `+ ${t('templates_list.add_trigger')}`}
        </button>
      ) : (
        <div className="flex gap-1 items-center">
          <input
            className="text-[10px] border border-border rounded px-1.5 py-0.5 w-14"
            placeholder="INT"
            value={type}
            onChange={(e) => setType(e.target.value.toUpperCase())}
            maxLength={12}
          />
          <input
            className="text-[10px] border border-border rounded px-1.5 py-0.5 w-14"
            placeholder="GRC"
            value={dept}
            onChange={(e) => setDept(e.target.value.toUpperCase())}
            maxLength={12}
          />
          <input
            className="text-[10px] border border-border rounded px-1.5 py-0.5 w-14"
            placeholder="FRM"
            value={form}
            onChange={(e) => setForm(e.target.value.toUpperCase())}
            maxLength={12}
          />
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="text-[12px] text-primary px-1"
          >
            ✓
          </button>
          <button
            onClick={() => setOpen(false)}
            className="text-[12px] text-muted px-1"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function InstantiateModal({ template, onClose }: { template: TemplateView; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState(template.title);
  const [message, setMessage] = useState(template.message ?? '');
  const [recipients, setRecipients] = useState<Record<string, { name: string; email: string }>>(
    () =>
      Object.fromEntries(
        template.recipients.map((r) => [r.id, { name: r.name, email: r.email ?? '' }]),
      ),
  );

  const instantiate = useMutation({
    mutationFn: () =>
      templatesApi.instantiate(template.id, {
        title: title.trim() || undefined,
        message: message.trim() || undefined,
        recipients: template.recipients.map((r) => ({
          templateRecipientId: r.id,
          name: recipients[r.id]?.name ?? r.name,
          email: recipients[r.id]?.email ?? '',
        })),
      }),
    onSuccess: (data) => {
      toast.success(t('templates_list.envelope_created'));
      navigate(`/envelopes/${data.envelopeId}`);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const allFilled = template.recipients.every(
    (r) =>
      (recipients[r.id]?.name ?? '').trim() !== '' &&
      (recipients[r.id]?.email ?? '').trim() !== '',
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('templates_list.use_template')}: {template.title}</h2>
          <button onClick={onClose} className="text-muted hover:text-gray-900 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="label">{t('templates_list.envelope_title_prompt')}</label>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <label className="label">{t('wizard.message_label')} <span className="text-muted font-normal">{t('common.optional')}</span></label>
            <textarea
              className="textarea min-h-[60px]"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div>
            <div className="label">{t('envelope.recipients')}</div>
            <p className="text-[12px] text-muted mb-3">{t('templates_list.recipients_help')}</p>
            <div className="space-y-2">
              {template.recipients.map((r) => (
                <div
                  key={r.id}
                  className="grid grid-cols-[28px_1fr_1fr_88px] gap-2 items-center"
                >
                  <span className="text-muted text-[12px] text-center">#{r.signingOrder}</span>
                  <input
                    className="input"
                    placeholder={t('recipients_step.name_label')}
                    value={recipients[r.id]?.name ?? ''}
                    onChange={(e) =>
                      setRecipients((prev) => ({
                        ...prev,
                        [r.id]: { ...prev[r.id], name: e.target.value },
                      }))
                    }
                  />
                  <input
                    className="input"
                    placeholder={t('recipients_step.email_placeholder')}
                    type="email"
                    value={recipients[r.id]?.email ?? ''}
                    onChange={(e) =>
                      setRecipients((prev) => ({
                        ...prev,
                        [r.id]: { ...prev[r.id], email: e.target.value },
                      }))
                    }
                  />
                  <span className="badge badge-muted text-[10px] text-center">{t(`recipients_step.role_${r.role}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
          <button
            onClick={() => instantiate.mutate()}
            disabled={!allFilled || instantiate.isPending}
            className="btn btn-primary"
          >
            {instantiate.isPending ? t('common.creating') : t('templates_list.create_envelope')}
          </button>
        </div>
      </div>
    </div>
  );
}
